import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Context } from 'Context/Context';
import useIsMobile from 'Hooks/useIsMobile';
import { caseTypeArray } from 'Components/Client/Cases/CaseFormFields';
import { STATE_SELECTION_ENABLED, DEFAULT_STATE, dateOfIncidentValues, getDefaultFilterApi, initialFilterValue, premiumFilterDataDisp, updateUserApi } from 'Constants';
import { getArrValue, getCaseType } from 'Utils';
import { C, Svg } from './CaseListUI';

// ---------------------------------------------------------------------------
// Case list filters (design 27b desktop popup, 27c–27f mobile screens).
// The popup edits a DRAFT copy of the filters; nothing is searched until
// "Show N cases". Removing a chip or "Clear all" on the list applies at once.
// Same filter shape / values as before, so every list API keeps working.
// ---------------------------------------------------------------------------

const ICO = {
  x: 'M6 6l12 12M18 6L6 18',
  chev: 'M6 9l6 6 6-6',
  back: 'M15 18l-6-6 6-6',
  lock: ['M5 11h14v10H5z', 'M8 11V7a4 4 0 0 1 8 0v4'],
  crown: 'M3 7l4.5 4L12 5l4.5 6L21 7l-2 11H5z',
  filter: 'M4 6h16M7 12h10M10 18h4',
  reset: ['M3 12a9 9 0 1 0 3-6.7', 'M3 4v5h5'],
  search: ['M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14z', 'M21 21l-4.3-4.3'],
  check: 'M5 12l5 5L20 7',
};

// dateOfIncident is stored as an object (same as the old FilterElement)
const monthData = (id) => {
  switch (id) {
    case '1': case '6': case '12': case '36': return { start: id, end: 0, id };
    case '-36': return { end: 36, id };
    default: return { start: 0, end: 0, id };
  }
};

const PREMIUM_META = {
  aiEstimateUpperValue: { label: 'Case estimate value', chip: 'Est. value' },
  isClassAction: { label: 'Class action', chip: 'Class action' },
  isEntity: { label: 'Entity or individual', chip: 'Against' },
  defendant: { label: 'Multiple plaintiffs', chip: 'Multiple plaintiffs' },
  aiDate: { label: 'Deadline to file', chip: 'Deadline' },
  aiDifficulty: { label: 'Difficulty', chip: 'Difficulty' },
  adversePartyPublicCompany: { label: 'Against public company', chip: 'Public company' },
  aiSuitability: { label: 'Funder suitability', chip: 'Funder fit' },
};

export function useFilterFields() {
  const { states } = useContext(Context);
  return useMemo(() => {
    const stateOpts = (states || []).map((s) => ({ id: s, name: s }));
    const basic = [
      { name: 'caseType', label: 'Type of case', kind: 'multi', search: true, options: caseTypeArray.map((c) => ({ id: c.id, name: c.name })) },
      ...(STATE_SELECTION_ENABLED ? [{ name: 'incidentState', label: 'Happened in (state)', kind: 'multi', search: true, options: stateOpts }] : []),
      { name: 'caseCity', label: 'Happened in (city)', kind: 'text' },
      { name: 'dateOfIncident', label: 'Date of incident', kind: 'single', date: true, options: dateOfIncidentValues.map((o) => ({ id: o.id, name: o.name })) },
      ...(STATE_SELECTION_ENABLED ? [{ name: 'clientState', label: "Client's state", kind: 'multi', search: true, options: stateOpts }] : []),
      { name: 'attAvailable', label: 'Represented by attorney', kind: 'single', options: [{ id: 'yes', name: 'Yes' }, { id: 'no', name: 'No' }] },
    ];
    const premium = premiumFilterDataDisp.map((p) => ({
      name: p.name, label: PREMIUM_META[p.name]?.label || p.title, chip: PREMIUM_META[p.name]?.chip || p.title,
      kind: 'single', premium: true, options: p.values.map((v) => ({ id: v.id, name: v.name })),
    }));
    return { basic, premium, all: [...basic, ...premium] };
  }, [states]);
}

export const normalizeFilters = (f) => {
  const out = {};
  Object.keys(initialFilterValue).forEach((k) => { out[k] = Array.isArray(f?.[k]) ? [...f[k]] : []; });
  return out;
};
export const activeFieldCount = (f) => Object.values(f || {}).filter((v) => Array.isArray(v) && v.length > 0).length;

const valueId = (field, v) => (field.date ? v?.id : v);
const optionName = (field, id) => field.options?.find((o) => String(o.id) === String(id))?.name;

// One human label per selected value (chips)
export function chipItems(filters, fields) {
  const items = [];
  fields.all.forEach((field) => {
    (filters?.[field.name] || []).forEach((v) => {
      let text;
      if (field.name === 'caseType') text = getCaseType(v) || v;
      else if (field.name === 'incidentState') text = v;
      else if (field.name === 'clientState') text = `Client: ${v}`;
      else if (field.name === 'caseCity') text = `City: ${v}…`;
      else if (field.name === 'dateOfIncident') text = `Incident: ${getArrValue(dateOfIncidentValues, v?.id, 'id', 'name') || '—'}`;
      else if (field.name === 'attAvailable') text = `Represented: ${v === 'yes' ? 'Yes' : 'No'}`;
      else text = `${field.chip}: ${optionName(field, v) || v}`;
      items.push({ field, value: v, text, premium: !!field.premium });
    });
  });
  return items;
}

const fieldText = (field, values) => {
  if (!values || values.length === 0) return null;
  if (field.kind === 'text') return `Starts with "${values[0]}"`;
  if (field.date) return optionName(field, values[0]?.id);
  return values.map((v) => optionName(field, v) || v).join(', ');
};

// ---------- debounced count for "Show N cases" + per-option counts ----------
function useDebouncedCall(active, draft, makeReq) {
  const { makeApiCallFunc } = useContext(Context);
  const [result, setResult] = useState({ loading: false, value: null });
  const seq = useRef(0);
  const key = JSON.stringify(draft);
  useEffect(() => {
    if (!active || !makeReq) return undefined;
    const req = makeReq(draft);
    if (!req) { setResult({ loading: false, value: null }); return undefined; }
    const mine = ++seq.current;
    setResult((r) => ({ ...r, loading: true }));
    const t = setTimeout(() => {
      makeApiCallFunc(req.url, req.body,
        ({ data }) => { if (mine === seq.current) setResult({ loading: false, value: req.pick ? req.pick(data) : data }); },
        () => { if (mine === seq.current) setResult({ loading: false, value: null }); },
        null, true);
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, key]);
  return result;
}

const showLabel = (count) => (typeof count === 'number' ? `Show ${count} ${count === 1 ? 'case' : 'cases'}` : 'Show cases');

// ---------- shared filter state helpers ----------
function useFilterActions({ attorney, setUpdateComp }) {
  const { filters, setFilters, setOffset, setIsFilterApplied, userDetails, makeApiCallFunc, setUpdateLoggedinData } = useContext(Context);
  const userInfo = attorney ? attorney?.userId : userDetails;
  useEffect(() => { if (setIsFilterApplied) setIsFilterApplied(activeFieldCount(filters) > 0); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => { setOffset(0); setUpdateComp && setUpdateComp((p) => !p); };
  const apply = (next) => { setFilters(normalizeFilters(next)); refresh(); };
  const remove = (field, value) => {
    setFilters((prev) => ({ ...prev, [field.name]: (prev[field.name] || []).filter((v) => (field.date ? v?.id !== value?.id : v !== value)) }));
    refresh();
  };
  const clearAll = () => { setFilters(normalizeFilters(initialFilterValue)); refresh(); };
  const loadDefaults = (cb) => {
    makeApiCallFunc(getDefaultFilterApi + '/' + userInfo?._id, null,
      (resp) => cb({ caseType: resp?.data?.casePref || [], incidentState: STATE_SELECTION_ENABLED ? (resp?.data?.statePref || []) : [] }),
      (err) => { console.log(err); toast.error('Could not load your default filters.'); }, null, true);
  };
  const saveDefaults = (draft) => {
    const casePref = draft.caseType || []; const statePref = STATE_SELECTION_ENABLED ? (draft.incidentState || []) : [DEFAULT_STATE];
    if (!userDetails?.isUserFunder && (casePref.length === 0 || statePref.length === 0)) {
      toast.warning(STATE_SELECTION_ENABLED ? 'Pick at least one case type and one state to save them as your default.' : 'Pick at least one case type to save it as your default.');
      return;
    }
    makeApiCallFunc(updateUserApi, { casePref, statePref, user: userDetails?._id },
      () => { setUpdateLoggedinData && setUpdateLoggedinData((p) => !p); toast.success('Saved as your default filters.'); },
      (err) => { console.log(err); toast.error('Could not save your default filters.'); });
  };
  return { filters, userInfo, apply, remove, clearAll, loadDefaults, saveDefaults };
}

// ===========================================================================
// Row above the list: "Filtering by:" chips · Edit filters · Clear all
// (mobile: navy "Filters · N" pill + chips, scrolls sideways)
// countFor(draft)  -> { url, body, pick(data) } for "Show N cases"
// facetsFor(draft) -> { url, body } returning { caseType:{id:n}, ... } (optional)
// ===========================================================================
export function FilterBar({ attorney, setUpdateComp, countFor, facetsFor, noPremium = false }) {
  const isMobile = useIsMobile();
  const fields = useFilterFields();
  const actions = useFilterActions({ attorney, setUpdateComp });
  const [open, setOpen] = useState(false);
  const chips = chipItems(actions.filters, fields);
  const n = activeFieldCount(actions.filters);

  const dialog = open && (isMobile
    ? <MobileFilters fields={fields} actions={actions} countFor={countFor} facetsFor={facetsFor} onClose={() => setOpen(false)} noPremium={noPremium} />
    : <FiltersDialog fields={fields} actions={actions} countFor={countFor} facetsFor={facetsFor} onClose={() => setOpen(false)} noPremium={noPremium} />);

  if (isMobile) {
    return (
      <>
        {dialog}
        <div className="hide-vertical-scrollbar flex gap-2 py-3 px-4 mt-0 -mx-[14px] mb-1 overflow-x-auto bg-cream">
          <span onClick={() => setOpen(true)} className="flex-none flex items-center gap-1.5 font-plexsans font-semibold text-[13px] leading-none text-ivory bg-navy rounded-[20px] py-2.5 px-3.5 whitespace-nowrap cursor-pointer">
            <Svg d={ICO.filter} size={14} width={2.2} stroke={C.ivory} />Filters{n ? ` · ${n}` : ''}
          </span>
          {chips.map((c, i) => (
            <span key={i} onClick={() => setOpen(true)} className={`flex-none font-plexsans font-medium text-[13px] leading-none whitespace-nowrap rounded-[20px] py-2.5 px-3 cursor-pointer ${c.premium ? 'text-brass bg-brass/[.08] border border-brass/[.3]' : 'text-navy bg-white border border-navy/[.2]'}`}>{c.text}</span>
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      {dialog}
      <div className="flex items-center gap-2.5 flex-wrap mb-[18px]">
        <span className="font-plexsans font-semibold text-[12.5px] leading-none text-navy/[.55] whitespace-nowrap">Filtering by:</span>
        {chips.length === 0 && <span className="font-plexsans font-normal text-[12.5px] leading-none text-navy/[.45]">All cases</span>}
        {chips.map((c, i) => (
          <span key={i} className={`flex items-center gap-1.5 font-plexsans font-medium text-[12.5px] leading-none rounded-[20px] py-[7px] px-3 whitespace-nowrap ${c.premium ? 'text-brass bg-brass/[.08] border border-brass/[.3]' : 'text-navy bg-white border border-navy/[.15]'}`}>
            {c.text}
            <span title="Remove" onClick={() => actions.remove(c.field, c.value)} className="flex cursor-pointer"><Svg d={ICO.x} size={10} width={3} stroke="rgba(20,33,61,.5)" /></span>
          </span>
        ))}
        <button type="button" onClick={() => setOpen(true)} className="hover:opacity-90 flex items-center gap-[7px] font-plexsans font-semibold text-[12.5px] leading-none text-ivory bg-navy border-0 rounded-[20px] py-2 px-[15px] cursor-pointer whitespace-nowrap">
          <Svg d={ICO.filter} size={14} width={2.2} stroke={C.ivory} />Edit filters
        </button>
        {chips.length > 0 &&
          <span onClick={actions.clearAll} className="font-plexsans font-medium text-[12.5px] leading-none text-danger underline cursor-pointer ml-auto">Clear all</span>}
      </div>
    </>
  );
}

// ===========================================================================
// Desktop popup (27b)
// ===========================================================================
function FiltersDialog({ fields, actions, countFor, facetsFor, onClose, noPremium }) {
  const { userDetails } = useContext(Context);
  const navigate = useNavigate();
  const [draft, setDraft] = useState(() => normalizeFilters(actions.filters));
  const [openField, setOpenField] = useState(null);
  const triggers = useRef({});   // field name -> trigger element, used to place the dropdown
  const [saveDefault, setSaveDefault] = useState(false);
  const premiumUser = !!actions.userInfo?.isActiveSubscription;
  const assistant = !!userDetails?.isUserAssistant;
  const count = useDebouncedCall(true, draft, countFor);
  const facets = useDebouncedCall(true, draft, facetsFor);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { if (openField) setOpenField(null); else onClose(); } };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [openField, onClose]);

  const set = (name, values) => setDraft((d) => ({ ...d, [name]: values }));
  const submit = () => {
    if (saveDefault && !assistant) actions.saveDefaults(draft);
    actions.apply(draft);
    onClose();
  };

  const fieldBox = (field, locked) => {
    const values = draft[field.name] || [];
    const text = fieldText(field, values);
    if (locked) {
      return (
        <div key={field.name} className="flex flex-col gap-[7px]">
          <span className="font-plexsans font-medium text-[12.5px] leading-[1.2] text-navy/[.45]">{field.label}</span>
          <div className="flex items-center justify-between gap-2.5 py-2.5 px-3 bg-navy/[.03] border border-dashed border-navy/[.2] rounded-md">
            <span className="font-plexsans font-normal text-[13.5px] leading-[1.2] text-navy/[.35]">Premium</span>
            <Svg d={ICO.lock} size={13} width={2.2} stroke="rgba(20,33,61,.35)" />
          </div>
        </div>
      );
    }
    const selBorder = text ? (field.premium ? 'border-brass' : 'border-navy/[.45]') : 'border-navy/[.18]';
    return (
      <div key={field.name} className="flex flex-col gap-[7px] relative">
        <span className="font-plexsans font-medium text-[12.5px] leading-[1.2] text-navy/[.7]">{field.label}</span>
        {field.kind === 'text' ? (
          <div className={`flex items-center gap-2 py-0 px-3 bg-white border ${selBorder} rounded-md`}>
            <input
              value={values[0] || ''}
              onChange={(e) => set(field.name, e.target.value.trim() ? [e.target.value] : [])}
              placeholder="Any"
              className="outline-none bg-transparent placeholder:text-[rgba(20,33,61,0.45)] flex-1 min-w-0 py-2.5 px-0 font-plexsans font-medium text-[13.5px] leading-[1.2] text-navy"
            />
            {values[0] && <span onClick={() => set(field.name, [])} className="flex cursor-pointer"><Svg d={ICO.x} size={12} width={2.6} stroke="rgba(20,33,61,.5)" /></span>}
          </div>
        ) : (
          <div
            ref={(el) => { triggers.current[field.name] = el; }}
            onClick={() => setOpenField(openField === field.name ? null : field.name)}
            className={`flex items-center justify-between gap-2.5 py-2.5 px-3 bg-white border ${openField === field.name ? 'border-navy' : selBorder} rounded-md cursor-pointer`}
          >
            <span className={`font-plexsans ${text ? 'font-medium text-navy' : 'font-normal text-navy/[.45]'} text-[13.5px] leading-[1.2] whitespace-nowrap overflow-hidden text-ellipsis`}>{text || 'Any'}</span>
            <Svg d={ICO.chev} size={14} width={2.2} stroke="rgba(20,33,61,.55)" className={openField === field.name ? 'rotate-180' : ''} />
          </div>
        )}
        {openField === field.name && (field.kind === 'multi'
          ? <MultiPopover anchor={triggers.current[field.name]} field={field} values={values} counts={facets.value?.[field.name]} onChange={(v) => set(field.name, v)} onClose={() => setOpenField(null)} />
          : <SinglePopover anchor={triggers.current[field.name]} field={field} values={values} onChange={(v) => { set(field.name, v); setOpenField(null); }} onClose={() => setOpenField(null)} />)}
      </div>
    );
  };

  const n = activeFieldCount(draft);
  return (
    <div className="font-plexsans fixed inset-0 z-[60]">
      <div onClick={onClose} className="absolute inset-0 bg-navy/[.45]" />
      <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[880px] max-w-[calc(100vw_-_32px)] max-h-[calc(100vh_-_80px)] bg-ivory rounded-lg shadow-[0_24px_60px_-20px_rgba(20,33,61,.5)] flex flex-col">
        {/* header */}
        <div className="flex-none flex items-center justify-between py-5 px-7 border-b border-b-navy/[.12]">
          <div className="flex items-baseline gap-2.5">
            <span className="font-plexsans font-semibold text-[19px] leading-none text-navy">Filters</span>
            <span className="font-plexsans font-medium text-[12.5px] leading-none text-navy/[.6]">{n} active</span>
          </div>
          <div className="flex items-center gap-4">
            <span onClick={() => actions.loadDefaults((def) => setDraft((d) => ({ ...d, ...def })))} className="hover:opacity-75 flex items-center gap-1.5 font-plexsans font-medium text-[12.5px] leading-none text-navy cursor-pointer">
              <Svg d={ICO.reset} size={13} width={2} stroke={C.navy} />{assistant ? "Apply attorney's defaults" : 'Apply my defaults'}
            </span>
            <span onClick={onClose} title="Close" className="flex cursor-pointer"><Svg d={ICO.x} size={16} width={2.2} stroke={C.navy} /></span>
          </div>
        </div>
        {/* body */}
        <div className={`flex-1 min-h-0 overflow-y-auto grid ${noPremium ? 'grid-cols-[minmax(0,1fr)]' : 'grid-cols-[minmax(0,1fr)_minmax(0,1fr)]'}`}>
          <div className={`pt-[18px] px-7 pb-6 flex flex-col gap-3.5 ${noPremium ? 'border-r-0' : 'border-r border-r-navy/[.1]'}`}>
            <div className="font-plexsans font-semibold text-[11px] leading-none tracking-[.08em] uppercase text-navy/[.55]">Basic filters</div>
            <div className="grid grid-cols-2 gap-y-4 gap-x-[18px]">
              {fields.basic.map((f) => fieldBox(f, false))}
            </div>
          </div>
          {!noPremium &&
            <div className="pt-[18px] px-7 pb-6 flex flex-col gap-3.5 bg-brass/[.04]">
              <div className="flex items-center gap-2 font-plexsans font-semibold text-[11px] leading-none tracking-[.08em] uppercase text-brass">
                <Svg d={premiumUser ? ICO.crown : ICO.lock} size={13} width={2.2} stroke={C.brass} />Premium filters
              </div>
              {!premiumUser &&
                <div className="flex items-center gap-3.5 py-[11px] px-3.5 bg-white border border-brass/[.35] rounded-md">
                  <div className="flex flex-col gap-1 min-w-0">
                    <span className="font-plexsans font-semibold text-[13px] leading-[1.3] text-navy">Narrow cases by value, difficulty and funder fit</span>
                    <span className="font-plexsans font-normal text-[12px] leading-[1.4] text-navy/[.65]">
                      {assistant ? 'The attorney needs a Premium membership for these. Basic filters still apply.' : `${fields.premium.length} premium filters come with Premium. Your basic filters still apply.`}
                    </span>
                  </div>
                  {!assistant &&
                    <button type="button" onClick={() => navigate('/attorney/subscription')} className="hover:bg-brassDark flex-none font-plexsans font-semibold text-[12.5px] leading-none text-ivory bg-brass border-0 rounded-md py-[9px] px-3.5 whitespace-nowrap cursor-pointer">Upgrade to Premium</button>}
                </div>}
              <div className="grid grid-cols-2 gap-y-4 gap-x-[18px]">
                {fields.premium.map((f) => fieldBox(f, !premiumUser))}
              </div>
            </div>}
        </div>
        {/* footer */}
        <div className="flex-none flex items-center justify-between gap-3.5 py-4 px-7 border-t border-t-navy/[.12] bg-white rounded-[0_0_8px_8px]">
          <div className="flex items-center gap-[18px]">
            {!assistant &&
              <div onClick={() => setSaveDefault((s) => !s)} className="flex items-center gap-2 font-plexsans font-medium text-[13px] leading-none text-navy cursor-pointer select-none">
                <Check checked={saveDefault} onChange={() => setSaveDefault((s) => !s)} />
                <span>Save <span className="font-semibold">case type + state</span> as my default</span>
              </div>}
            <span onClick={() => setDraft(normalizeFilters(initialFilterValue))} className="font-plexsans font-medium text-[13px] leading-none text-danger underline cursor-pointer">Clear all</span>
          </div>
          <button type="button" onClick={submit} className="hover:bg-brassDark transition-colors font-plexsans font-semibold text-[13.5px] leading-none text-ivory bg-brass border-0 rounded-md py-[13px] px-[22px] cursor-pointer whitespace-nowrap min-w-[150px]">
            {count.loading && typeof count.value !== 'number' ? 'Show cases' : showLabel(count.value)}
          </button>
        </div>
      </div>
    </div>
  );
}

// Box sizes used by <Check> (16 in the desktop dropdown, 17 default, 18 on mobile)
const CHECK_SIZE = { 16: 'w-4 h-4', 17: 'w-[17px] h-[17px]', 18: 'w-[18px] h-[18px]' };

function Check({ checked, onChange, size = 17 }) {
  return (
    <span
      role="checkbox"
      aria-checked={checked}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(); }}
      className={`${CHECK_SIZE[size] || ''} flex-none rounded-[3px] border-[1.5px] ${checked ? 'border-navy bg-navy' : 'border-navy/[.35] bg-white'} flex items-center justify-center`}
      style={CHECK_SIZE[size] ? undefined : { width: size + 'px', height: size + 'px' }}
    >
      {checked && <Svg d={ICO.check} size={size - 5} width={3} stroke="#fff" />}
    </span>
  );
}

// Dropdowns render in a portal on <body> with position: fixed, so they sit on top of
// the dialog instead of being clipped by its scrolling body / footer, and never
// widen the page. Placed under the trigger (or above it when there's no room),
// kept inside the viewport, closed on outside click or when the dialog scrolls.
const POPOVER_CLS = 'fixed z-[70] box-border bg-white border border-navy/[.15] rounded-lg shadow-[0_16px_36px_-12px_rgba(20,33,61,.4)]';

function usePopover(anchor, onClose) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const place = () => {
    const el = ref.current;
    if (!anchor || !el) return;
    const r = anchor.getBoundingClientRect();
    const vw = window.innerWidth, vh = window.innerHeight, gap = 6, edge = 8;
    const width = Math.min(Math.max(r.width, 260), vw - edge * 2);
    const left = Math.min(Math.max(r.left, edge), vw - width - edge);
    const below = vh - r.bottom - gap - edge;
    const above = r.top - gap - edge;
    const natural = el.scrollHeight;
    const up = natural > below && above > below;
    const maxHeight = Math.max(Math.min(up ? above : below, 420), 140);
    const h = Math.min(natural, maxHeight);
    setPos({ left, width, maxHeight, top: up ? r.top - gap - h : r.bottom + gap });
  };
  useLayoutEffect(place, [anchor]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const down = (e) => {
      if (ref.current && !ref.current.contains(e.target) && !(anchor && anchor.contains(e.target))) onClose();
    };
    const scroll = (e) => { if (!(ref.current && ref.current.contains(e.target))) onClose(); };
    document.addEventListener('mousedown', down);
    window.addEventListener('scroll', scroll, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', down);
      window.removeEventListener('scroll', scroll, true);
      window.removeEventListener('resize', place);
    };
  }, [anchor, onClose]); // eslint-disable-line react-hooks/exhaustive-deps
  // static look -> classes; only the measured position/size stays inline
  const className = `${POPOVER_CLS} ${pos ? '' : 'top-0 left-0 opacity-0 pointer-events-none'}`;
  const style = pos ? { top: pos.top + 'px', left: pos.left + 'px', width: pos.width + 'px', maxHeight: pos.maxHeight + 'px' } : undefined;
  return { ref, style, className, place };
}

function SinglePopover({ anchor, field, values, onChange, onClose }) {
  const { ref, style, className } = usePopover(anchor, onClose);
  const cur = field.date ? values[0]?.id : values[0];
  const row = (id, name) => {
    const sel = id === null ? cur == null : String(cur) === String(id);
    return (
      <div key={String(id)} onClick={() => onChange(id === null ? [] : [field.date ? monthData(id) : id])} className={`hover:bg-[rgba(20,33,61,0.04)] flex items-center justify-between gap-2.5 py-2.5 px-3 rounded-[5px] cursor-pointer font-plexsans ${sel ? 'font-semibold' : 'font-normal'} text-[13.5px] leading-[1.3] ${id === null ? 'text-navy/[.55]' : 'text-navy'}`}>
        {name}
        {sel && <Svg d={ICO.check} size={14} width={2.6} stroke={field.premium ? C.brass : C.navy} />}
      </div>
    );
  };
  return (
    createPortal(
      <div ref={ref} className={`font-plexsans ${className} p-1.5 overflow-y-auto`} style={style}>
        {row(null, 'Any')}
        {field.options.map((o) => row(o.id, o.name))}
      </div>,
      document.body
    )
  );
}

function MultiPopover({ anchor, field, values, counts, onChange, onClose }) {
  const { ref, style, className } = usePopover(anchor, onClose);
  const [q, setQ] = useState('');
  const list = field.options.filter((o) => !q || o.name.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => onChange(values.includes(id) ? values.filter((v) => v !== id) : [...values, id]);
  return (
    createPortal(
    <div ref={ref} className={`font-plexsans ${className} flex flex-col`} style={style}>
      {field.search &&
        <div className="flex-none pt-2.5 px-2.5 pb-1.5">
          <div className="flex items-center gap-2 py-2 px-2.5 border border-navy/[.18] rounded-md">
            <Svg d={ICO.search} size={13} width={2} stroke="rgba(20,33,61,.45)" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="outline-none bg-transparent placeholder:text-[rgba(20,33,61,0.4)] flex-1 min-w-0 font-plexsans font-normal text-[13px] leading-[1.2] text-navy" />
          </div>
        </div>}
      <div className="flex-auto min-h-0 max-h-[240px] overflow-y-auto py-1 px-1.5">
        {list.length === 0 && <div className="py-3.5 px-2.5 font-plexsans font-normal text-[13px] leading-[1.3] text-navy/[.5]">No matches</div>}
        {list.map((o) => {
          const sel = values.includes(o.id);
          const n = counts ? (counts[o.id] || 0) : null;
          return (
            <div key={o.id} onClick={() => toggle(o.id)} className="hover:bg-[rgba(20,33,61,0.04)] flex items-center gap-2.5 py-[9px] px-2 rounded-[5px] cursor-pointer">
              <Check checked={sel} onChange={() => toggle(o.id)} size={16} />
              <span className={`flex-1 font-plexsans ${sel ? 'font-medium' : 'font-normal'} text-[13.5px] leading-[1.3] text-navy`}>{o.name}</span>
              {n !== null && <span className="font-plexsans font-medium text-[12px] leading-none text-navy/[.45]">{n}</span>}
            </div>
          );
        })}
      </div>
      <div className="flex-none flex items-center justify-between py-2.5 px-3 border-t border-t-navy/[.1]">
        <span onClick={() => onChange([])} className={`font-plexsans font-medium text-[12.5px] leading-none ${values.length ? 'text-danger' : 'text-navy/[.35]'} cursor-pointer`}>Clear</span>
        <button type="button" onClick={onClose} className="font-plexsans font-semibold text-[12.5px] leading-none text-ivory bg-navy border-0 rounded-[5px] py-2 px-3.5 cursor-pointer">Done</button>
      </div>
    </div>,
    document.body
    )
  );
}

// ===========================================================================
// Mobile (27c–27f): full-screen list → long-list screen / bottom sheet
// ===========================================================================
function MobileFilters({ fields, actions, countFor, facetsFor, onClose, noPremium }) {
  const { userDetails } = useContext(Context);
  const navigate = useNavigate();
  const [draft, setDraft] = useState(() => normalizeFilters(actions.filters));
  const [screen, setScreen] = useState(null);   // field for the long-list screen
  const [sheet, setSheet] = useState(null);     // field for the bottom sheet
  const [saveDefault, setSaveDefault] = useState(false);
  const premiumUser = !!actions.userInfo?.isActiveSubscription;
  const assistant = !!userDetails?.isUserAssistant;
  const count = useDebouncedCall(true, draft, countFor);
  const facets = useDebouncedCall(true, draft, facetsFor);
  const set = (name, values) => setDraft((d) => ({ ...d, [name]: values }));

  useEffect(() => {
    const prev = document.body.style.overflow; document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const submit = () => {
    if (saveDefault && !assistant) actions.saveDefaults(draft);
    actions.apply(draft);
    onClose();
  };

  const row = (field, locked) => {
    const text = fieldText(field, draft[field.name]);
    return (
      <div
        key={field.name}
        onClick={() => { if (locked) return; if (field.kind === 'single') setSheet(field); else setScreen(field); }}
        className={`flex items-center justify-between gap-3 min-h-[52px] border-b border-b-navy/[.08] ${locked ? 'cursor-default' : 'cursor-pointer'}`}
      >
        <span className={`font-plexsans font-medium text-[15px] leading-[1.3] ${locked ? 'text-navy/[.45]' : 'text-navy'}`}>{field.label}</span>
        <span className="flex items-center gap-2 min-w-0">
          {locked
            ? <Svg d={ICO.lock} size={14} width={2.2} stroke="rgba(20,33,61,.35)" />
            : <>
                {text
                  ? <span className="font-plexsans font-medium text-[13.5px] leading-[1.2] text-brass whitespace-nowrap overflow-hidden text-ellipsis max-w-[150px]">{text}</span>
                  : <span className="font-plexsans font-normal text-[13.5px] leading-none text-navy/[.4]">Any</span>}
                <Svg d="M9 6l6 6-6 6" size={14} width={2.2} stroke="rgba(20,33,61,.55)" />
              </>}
        </span>
      </div>
    );
  };

  return (
    <div className="font-plexsans fixed inset-0 z-[60] bg-ivory flex flex-col">
      {/* top bar */}
      <div className="flex-none flex items-center justify-between py-3.5 px-[18px] border-b border-b-navy/[.12]">
        <span onClick={onClose} className="flex cursor-pointer min-w-[70px]"><Svg d={ICO.x} size={20} width={2.2} stroke={C.navy} /></span>
        <span className="font-plexsans font-semibold text-[17px] leading-none text-navy">Filters</span>
        <span onClick={() => actions.loadDefaults((def) => setDraft((d) => ({ ...d, ...def })))} className="font-plexsans font-medium text-[14px] leading-none text-brass min-w-[70px] text-right cursor-pointer">Defaults</span>
      </div>
      {/* list */}
      <div className="flex-1 min-h-0 overflow-auto pt-0 px-[18px] pb-4">
        <div className="pt-5 px-0 pb-1.5 font-plexsans font-semibold text-[11px] leading-none tracking-[.08em] uppercase text-navy/[.55]">Basic filters</div>
        {fields.basic.map((f) => row(f, false))}
        {!noPremium && <>
          <div className="flex items-center gap-[7px] pt-5 px-0 pb-1.5 font-plexsans font-semibold text-[11px] leading-none tracking-[.08em] uppercase text-brass">
            <Svg d={premiumUser ? ICO.crown : ICO.lock} size={13} width={2.2} stroke={C.brass} />Premium filters
          </div>
          {!premiumUser &&
            <div className="flex items-center justify-between gap-2.5 py-3 px-3.5 mt-1.5 mx-0 mb-1 bg-white border border-brass/[.35] rounded-md">
              <span className="font-plexsans font-medium text-[13px] leading-[1.35] text-navy">{assistant ? 'The attorney needs Premium for these' : `${fields.premium.length} premium filters come with Premium`}</span>
              {!assistant && <button type="button" onClick={() => navigate('/attorney/subscription')} className="flex-none font-plexsans font-semibold text-[12.5px] leading-none text-ivory bg-brass border-0 rounded-md py-[9px] px-3">Upgrade</button>}
            </div>}
          {fields.premium.map((f) => row(f, !premiumUser))}
        </>}
        {!assistant &&
          <div onClick={() => setSaveDefault((s) => !s)} className="flex items-center gap-2.5 pt-[18px] px-0 pb-1 font-plexsans font-medium text-[14px] leading-[1.3] text-navy cursor-pointer select-none">
            <Check checked={saveDefault} onChange={() => setSaveDefault((s) => !s)} size={18} />
            <span>Save <span className="font-semibold">case type + state</span> as my default</span>
          </div>}
      </div>
      {/* footer */}
      <div className="flex-none flex gap-2.5 pt-3.5 px-[18px] pb-[26px] border-t border-t-navy/[.12] bg-white">
        <button type="button" onClick={() => setDraft(normalizeFilters(initialFilterValue))} className="flex-none font-plexsans font-semibold text-[14px] leading-none text-navy bg-white border border-navy/[.25] rounded-lg py-0 px-[18px] h-12">Clear all</button>
        <button type="button" onClick={submit} className="flex-1 font-plexsans font-semibold text-[15px] leading-none text-ivory bg-brass border-0 rounded-lg h-12 cursor-pointer">
          {count.loading && typeof count.value !== 'number' ? 'Show cases' : showLabel(count.value)}
        </button>
      </div>

      {screen && <MobileListScreen field={screen} values={draft[screen.name] || []} counts={facets.value?.[screen.name]} onChange={(v) => set(screen.name, v)} onBack={() => setScreen(null)} />}
      {sheet && <MobileSheet field={sheet} values={draft[sheet.name] || []} onPick={(v) => { set(sheet.name, v); setSheet(null); }} onClose={() => setSheet(null)} />}
    </div>
  );
}

function MobileListScreen({ field, values, counts, onChange, onBack }) {
  const [q, setQ] = useState('');
  const isText = field.kind === 'text';
  const list = isText ? [] : field.options.filter((o) => !q || o.name.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id) => onChange(values.includes(id) ? values.filter((v) => v !== id) : [...values, id]);
  return (
    <div className="absolute inset-0 z-[2] bg-ivory flex flex-col">
      <div className="flex-none flex items-center justify-between py-3.5 px-[18px] border-b border-b-navy/[.12]">
        <span onClick={onBack} className="flex items-center gap-0.5 font-plexsans font-medium text-[14px] leading-none text-brass cursor-pointer min-w-[70px]"><Svg d={ICO.back} size={18} width={2.2} stroke={C.brass} />Filters</span>
        <span className="font-plexsans font-semibold text-[16px] leading-none text-navy">{field.label}</span>
        <span onClick={() => onChange([])} className={`font-plexsans font-medium text-[14px] leading-none ${values.length ? 'text-danger' : 'text-navy/[.35]'} min-w-[70px] text-right cursor-pointer`}>Clear</span>
      </div>
      <div className="pt-3 px-[18px] pb-1.5">
        <div className="flex items-center gap-2 py-[11px] px-3 bg-white border border-navy/[.18] rounded-lg">
          <Svg d={ICO.search} size={15} width={2} stroke="rgba(20,33,61,.45)" />
          {isText
            ? <input autoFocus value={values[0] || ''} onChange={(e) => onChange(e.target.value.trim() ? [e.target.value] : [])} placeholder="City starts with…" className="outline-none bg-transparent flex-1 min-w-0 font-plexsans font-normal text-[15px] leading-[1.2] text-navy" />
            : <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search" className="outline-none bg-transparent flex-1 min-w-0 font-plexsans font-normal text-[15px] leading-[1.2] text-navy" />}
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-auto pt-0 px-[18px] pb-5">
        {list.map((o) => {
          const sel = values.includes(o.id);
          const n = counts ? (counts[o.id] || 0) : null;
          return (
            <div key={o.id} onClick={() => toggle(o.id)} className="flex items-center gap-3 min-h-[50px] border-b border-b-navy/[.08] cursor-pointer">
              <Check checked={sel} onChange={() => toggle(o.id)} size={18} />
              <span className={`flex-1 font-plexsans ${sel ? 'font-medium' : 'font-normal'} text-[15px] leading-[1.3] text-navy`}>{o.name}</span>
              {n !== null && <span className="font-plexsans font-medium text-[13px] leading-none text-navy/[.45]">{n}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MobileSheet({ field, values, onPick, onClose }) {
  const cur = field.date ? values[0]?.id : values[0];
  const chip = (id, name) => {
    const sel = id === null ? cur == null : String(cur) === String(id);
    const tone = sel ? (field.premium ? 'text-ivory bg-brass border-brass' : 'text-ivory bg-navy border-navy') : 'text-navy bg-white border-navy/[.2]';
    return (
      <span key={String(id)} onClick={() => onPick(id === null ? [] : [field.date ? monthData(id) : id])}
        className={`font-plexsans font-medium text-[15px] leading-none py-[13px] px-4 rounded-[24px] cursor-pointer border ${tone}`}>
        {name}
      </span>
    );
  };
  return (
    <div className="absolute inset-0 z-[3]">
      <div onClick={onClose} className="absolute inset-0 bg-navy/[.45]" />
      <div className="absolute left-0 right-0 bottom-0 bg-ivory rounded-[14px_14px_0_0] pt-[18px] px-[18px] pb-[30px] shadow-[0_-12px_32px_-12px_rgba(20,33,61,.4)]">
        <div className="w-9 h-1 rounded-sm bg-navy/[.2] mt-0 mx-auto mb-3.5" />
        <div className="font-plexsans font-semibold text-[16px] leading-[1.2] text-navy mb-4">{field.label}</div>
        <div className="flex flex-wrap gap-2.5">
          {chip(null, 'Any')}
          {field.options.map((o) => chip(o.id, o.name))}
        </div>
      </div>
    </div>
  );
}
